// @ts-nocheck
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';

import ConfirmModal from './ConfirmModal';
import DialogTransition from '../Transition/DialogTransition';
import { SESSION_EVENTS } from '../../../const/CommonConst';
import {
  endContinueTransaction,
  startContinueTransaction,
} from '../../../utils/apmTransaction';

// Countdown duration in seconds
const LOGOUT_COUNTDOWN_SECONDS = 3;

/**
 * SessionLogoutModal Component
 *
 * Displays a modal informing the user that their session has fully expired
 * and they will be logged out. Auto-closes after countdown.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onAcknowledge - Callback when countdown completes
 * @returns {JSX.Element} The SessionLogoutModal component
 */
const SessionLogoutModal = ({ open, onAcknowledge }) => {
  const [countdown, setCountdown] = useState(LOGOUT_COUNTDOWN_SECONDS);
  const timerRef = useRef(null);

  /**
   * Handles cleanup and triggers acknowledge callback
   */
  const handleComplete = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onAcknowledge();
  }, [onAcknowledge]);

  /**
   * Handles manual close (clicking X button)
   */
  const handleClose = useCallback(
    (e) => {
      e?.stopPropagation();
      handleComplete();
    },
    [handleComplete]
  );

  // Start countdown when modal opens
  useEffect(() => {
    if (open) {
      // Reset countdown when modal opens
      setCountdown(LOGOUT_COUNTDOWN_SECONDS);

      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            // Countdown finished, trigger completion
            handleComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [open, handleComplete]);

  return (
    <Box>
      <Dialog
        maxWidth="xs"
        open={open}
        onClose={handleClose}
        TransitionComponent={DialogTransition}
      >
        <DialogTitle>
          <Box component="span" aria-live="polite" aria-atomic="true">
            Session Expired
          </Box>
        </DialogTitle>

        <IconButton
          color="primary"
          aria-label="Close dialog"
          title="Close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            py={2}
            gap={2}
          >
            <Typography variant="body1" align="center">
              Your session is expired. Logging out in {countdown}...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

SessionLogoutModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onAcknowledge: PropTypes.func.isRequired,
};

/**
 * SessionExpiredModal Component
 *
 * This component listens for token expiration events dispatched from Axios interceptors
 * and displays a confirmation modal to the user. Based on user's choice:
 * - Yes: Dispatches an event to refresh the token
 * - No: Dispatches an event to logout the user
 *
 * Also handles the case when refresh token is expired by showing a logout notification modal.
 *
 * @component
 * @returns {JSX.Element} The SessionExpiredModal component
 */
const SessionExpiredModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const isModalShownRef = useRef(false);
  const isLogoutModalShownRef = useRef(false);
  const continueTransactionRef = useRef(null);

  /**
   * Dismiss both expiry dialogs after the request owner detects a shared renewal.
   * Closing the logout dialog also cleans up its countdown through its effect.
   * Do not dispatch USER_DECLINED/SESSION_LOGOUT_ACKNOWLEDGED during recovery.
   * @returns {void}
   */
  const handleSessionRefreshed = useCallback(() => {
    endContinueTransaction(continueTransactionRef.current, 'dismissed');
    continueTransactionRef.current = null;
    setIsOpen(false);
    setIsLogoutModalOpen(false);
    isModalShownRef.current = false;
    isLogoutModalShownRef.current = false;
  }, []);

  /**
   * Handles the token expired event from Axios interceptor
   * Shows the modal only if it's not already shown
   */
  const handleTokenExpired = useCallback(() => {
    if (!isModalShownRef.current) {
      isModalShownRef.current = true;
      continueTransactionRef.current = startContinueTransaction();
      setIsOpen(true);
    }
  }, [setIsOpen]);

  /**
   * Handles the session fully expired event (refresh token expired)
   * Shows the logout notification modal
   */
  const handleSessionFullyExpired = useCallback(() => {
    if (!isLogoutModalShownRef.current) {
      isLogoutModalShownRef.current = true;
      setIsLogoutModalOpen(true);
    }
  }, [setIsLogoutModalOpen]);

  /**
   * Handles user confirmation (Yes button click)
   * Dispatches event to refresh the token
   */
  const handleConfirm = useCallback(() => {
    endContinueTransaction(continueTransactionRef.current, 'yes');
    continueTransactionRef.current = null;
    setIsOpen(false);
    isModalShownRef.current = false;

    // Dispatch event to notify Axios to refresh the token
    const confirmEvent = new CustomEvent(SESSION_EVENTS.USER_CONFIRMED);
    document.dispatchEvent(confirmEvent);
  }, [setIsOpen]);

  /**
   * Handles user cancellation (No button click)
   * Dispatches event to logout the user
   */
  const handleCancel = useCallback(() => {
    endContinueTransaction(continueTransactionRef.current, 'no');
    continueTransactionRef.current = null;
    setIsOpen(false);
    isModalShownRef.current = false;

    // Dispatch event to notify Axios to logout the user
    const declineEvent = new CustomEvent(SESSION_EVENTS.USER_DECLINED);
    document.dispatchEvent(declineEvent);
  }, [setIsOpen]);

  /**
   * Handles modal close (clicking outside or X button)
   * Treats as cancellation and logs out the user
   */
  const handleClose = useCallback(() => {
    handleCancel();
  }, [handleCancel]);

  /**
   * Handles user acknowledgement of session expiry logout modal
   * Dispatches event to proceed with logout
   */
  const handleLogoutAcknowledge = useCallback(() => {
    setIsLogoutModalOpen(false);
    isLogoutModalShownRef.current = false;

    // Dispatch event to notify Axios to proceed with logout
    const acknowledgeEvent = new CustomEvent(
      SESSION_EVENTS.SESSION_LOGOUT_ACKNOWLEDGED
    );
    document.dispatchEvent(acknowledgeEvent);
  }, [setIsLogoutModalOpen]);

  // Set up event listeners for token expiration events
  useEffect(() => {
    document.addEventListener(
      SESSION_EVENTS.SESSION_REFRESHED,
      handleSessionRefreshed
    );
    document.addEventListener(SESSION_EVENTS.TOKEN_EXPIRED, handleTokenExpired);
    document.addEventListener(
      SESSION_EVENTS.SESSION_FULLY_EXPIRED,
      handleSessionFullyExpired
    );

    return () => {
      document.removeEventListener(
        SESSION_EVENTS.SESSION_REFRESHED,
        handleSessionRefreshed
      );
      document.removeEventListener(
        SESSION_EVENTS.TOKEN_EXPIRED,
        handleTokenExpired
      );
      document.removeEventListener(
        SESSION_EVENTS.SESSION_FULLY_EXPIRED,
        handleSessionFullyExpired
      );
    };
  }, [handleSessionRefreshed, handleTokenExpired, handleSessionFullyExpired]);

  return (
    <>
      <ConfirmModal
        open={isOpen}
        handleClose={handleClose}
        confirm={handleConfirm}
        cancel={handleCancel}
        alertLabelText="Your session has expired. Would you like to continue your session?"
        confirmButtonLabel="Yes"
        cancelButtonLabel="No"
        resourceName="Session Expired"
      />
      <SessionLogoutModal
        open={isLogoutModalOpen}
        onAcknowledge={handleLogoutAcknowledge}
      />
    </>
  );
};

export default SessionExpiredModal;
